import React from "react";
import {Alert, Avatar, Box, Button, CircularProgress, List, ListItem, ListItemButton, ListItemText, Pagination, Stack, Tab, Tabs, Typography} from "@mui/material";
import {CheckCircleOutline as CheckCircle, ErrorOutline as Error, InfoOutlined as Info, LocalShippingOutlined as LocalShipping,
    NotificationsOutlined as NotificationsNone, ReceiptLongOutlined as ReceiptLong, WarningAmberOutlined as WarningAmber} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useNotifications} from "./notification-context";
import {notificationDay, notificationText, notificationTime} from "./notification-presentation";

const eventStyle = type => ({
    NEW_ORDER: {Icon: ReceiptLong, color: "primary.main", bg: "action.hover"},
    ORDER_STATUS_CHANGE: {Icon: LocalShipping, color: "primary.main", bg: "action.hover"},
    SUCCESS: {Icon: CheckCircle, color: "success.main", bg: "action.hover"},
    ERROR: {Icon: Error, color: "error.main", bg: "action.hover"},
    WARNING: {Icon: WarningAmber, color: "warning.main", bg: "action.hover"},
}[type] || {Icon: Info, color: "text.secondary", bg: "action.hover"});

export function NotificationFilters({feed, fullWidth = false}) {
    const {t} = useTranslation();
    return <Tabs value={feed.filter} onChange={(_, value) => feed.changeFilter(value)} variant={fullWidth ? "fullWidth" : "standard"}
        sx={{flexShrink: 0}}
        aria-label={t("NotificationFilters")}>
        <Tab value="all" label={t("All")}/><Tab value="unread" label={t("unread")}/>
    </Tabs>;
}
export default function NotificationFeed({feed, onOpen}) {
    const {t, i18n} = useTranslation(), center = useNotifications();
    const rows = feed.data?.content || [];
    return <Box aria-busy={feed.loading} sx={{minWidth: 0}}>
        {feed.error && <Alert severity="error" action={<Button color="inherit" onClick={feed.refresh}>{t("Retry")}</Button>}>{t(feed.error)}</Alert>}
        {feed.loading && <Box sx={{height: 3}}><CircularProgress size={16} aria-label={t("LoadingNotifications")}/></Box>}
        {!rows.length && !feed.loading && !feed.error && <Stack alignItems="center" spacing={1} sx={{px: 3, py: 7, textAlign: "center"}}>
            <NotificationsNone sx={{fontSize: 40, color: "text.disabled"}}/>
            <Typography fontWeight={600}>{t("NoNotifications")}</Typography>
            <Typography color="text.secondary" variant="body2">{t(feed.search.trim() ? "NotificationEmptySearch"
                : feed.filter === "unread" ? "UnreadNotificationsDesc" : "AllNotificationsDesc")}</Typography>
        </Stack>}
        <List disablePadding aria-label={t("Notifications")}>
            {rows.map((row, index) => {
                const day = notificationDay(row.notificationDate), unread = !row.read && !center.readIds.has(row.notificationId);
                const {Icon, color, bg} = eventStyle(row.notificationType);
                return <React.Fragment key={row.notificationId}>
                    {(!index || day !== notificationDay(rows[index - 1].notificationDate)) &&
                        <Typography component="li" variant="caption" sx={{listStyle: "none", px: 2.5, pt: 2, pb: 0.5,
                            fontWeight: 600, letterSpacing: "normal", color: "text.secondary"}}>{t(day)}</Typography>}
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => onOpen(row)} sx={{alignItems: "flex-start", gap: 1.5, px: 2.5, py: 1.5,
                            bgcolor: unread ? "action.hover" : "transparent", borderBottom: 1, borderColor: "divider",
                            "&:focus-visible": {outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2}}}>
                            <Avatar variant="rounded" sx={{width: 36, height: 36, bgcolor: bg, color, mt: 0.25}}><Icon fontSize="small"/></Avatar>
                            <ListItemText sx={{m: 0, minWidth: 0}} disableTypography
                                primary={<Typography variant="body2" fontWeight={unread ? 700 : 400} sx={{overflowWrap: "anywhere"}}>{notificationText(row, t)}</Typography>}
                                secondary={<>
                                    {(row.orderNumber || row.title) && <Typography variant="body2" color="text.secondary" sx={{overflowWrap: "anywhere", mt: 0.25}}>
                                        {[row.orderNumber, row.title].filter(Boolean).join(" · ")}</Typography>}
                                    <Typography component="time" dateTime={row.notificationDate} variant="caption" color="text.secondary">
                                        {notificationTime(row.notificationDate, i18n.language)}</Typography>
                                </>}/>
                            {unread && <Box component="span" aria-label={t("unread")} sx={{width: 7, height: 7, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0, mt: 1}}/>}
                        </ListItemButton>
                    </ListItem>
                </React.Fragment>;
            })}
        </List>
        {feed.data?.totalPages > 1 && <Box sx={{py: 2, display: "flex", justifyContent: "center"}}>
            <Pagination size="small" count={feed.data.totalPages} page={feed.page + 1} siblingCount={0}
                getItemAriaLabel={(type, value, selected) => type === "page"
                    ? t(selected ? "NotificationCurrentPage" : "NotificationGoToPage", {page: value})
                    : type === "previous" ? t("NotificationPreviousPage") : type === "next" ? t("NotificationNextPage") : undefined}
                onChange={(_, value) => feed.setPage(value - 1)} aria-label={t("NotificationPages")}/>
        </Box>}
    </Box>;
}
